import { Component, effect, inject, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { DirectLineService } from './services/directline.service';
import { DirectLineActivityType, DirectLineRole, SuggestedAction } from './models/directline.models';

@Component({
    selector: 'app-scout-ai-session',
    standalone: false,
    templateUrl: './scout-ai-session.component.html',
    styleUrls: ['./scout-ai-session.component.css', './scout-ai-shared.css']
})
export class ScoutAiSessionComponent {

    private authService = inject(MsalService);
    public directLineService = inject(DirectLineService);

    isLoading = signal(true);
    errorMessage = signal('');
    userId: string = '';
    private messageSentAt: number = 0;
    private readonly CONNECT_ERROR = 'Unable to connect to assistant. Please try again.';

    constructor() {
        effect(() => {
            const activities = this.directLineService.activities();
            if (activities.length === 0) return;
            const last = activities[activities.length - 1];
            if (last.from?.role === DirectLineRole.BOT && last.type === DirectLineActivityType.MESSAGE) {
                if (this.messageSentAt > 0) {
                    last.responseTimeSeconds = Math.round((Date.now() - this.messageSentAt) / 100) / 10;
                    this.messageSentAt = 0;
                }
                this.isLoading.set(false);
            }
        });
    }

    async connect(): Promise<void> {
        this.isLoading.set(true);
        this.errorMessage.set('');

        try {
            if (this.directLineService.connectionStatus() === 'disconnected') {
                this.userId = await this.directLineService.initialize(this.authService);

                if (this.directLineService.getStoredSession() && this.directLineService.activities().length > 0) {
                    this.isLoading.set(false);
                }
            } else {
                this.isLoading.set(false);
            }
        } catch {
            this.errorMessage.set(this.CONNECT_ERROR);
            this.isLoading.set(false);
        }
    }

    retry(): void {
        this.connect();
    }

    onSendMessage(text: string): void {
        this.messageSentAt = Date.now();
        this.directLineService.sendMessage(text);
    }

    onSuggestedActionClick(action: SuggestedAction): void {
        this.directLineService.handleSuggestedAction(action);
    }

    onNewConversation(): void {
        this.directLineService.disconnect();
        this.directLineService.clearHistory();
        this.connect();
    }
}
